import * as React from 'react';
import { useState, useContext } from 'react';
import {
  Box,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import UserForm from './UserForm';
import DeleteDialog from '../utils/components/DeleteDialog';

interface UserColumnProps {
  filteredUsers: User[];
  selectedUserId: number | null;
  onUserClick: (user: User) => void;
}

const UserColumn: React.FC<UserColumnProps> = ({
  filteredUsers,
  selectedUserId,
  onUserClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    getUsers,
    editUser,
    editUserLoading,
    deleteUser,
    deleteUserLoading,
  } = useContext(UserContext);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteOpen(true);
    handleMenuClose();
  };

  const handleEditSubmit = (data: UserFormDto) => {
    if (selectedUser) {
      editUser(selectedUser.id, data, () => {
        setEditOpen(false);
        getUsers(true);
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteUser(selectedUser.id, () => {
        setDeleteOpen(false);
        getUsers(true);
      });
    }
  };

  return (
    <Grid
      size={{ xs: 3 }}
      sx={{
        borderRight: theme.palette.border,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {filteredUsers.map((user) => (
        <Box
          key={user.id}
          onClick={() => onUserClick(user)}
          sx={{
            display: 'flex',
            height: '30px',
            minHeight: '30px',
            paddingLeft: '12px',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            ...theme.typography.usersFontTitle,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            margin: 0,
            mb: 1,
            backgroundColor: selectedUserId === user.id ? theme.palette.selectedBackground : 'transparent',
            borderRadius: '4px',
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexGrow: 1,
            }}
          >
            {user.email}
          </Box>
          {selectedUserId === user.id && (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, user)}
              sx={{ color: 'green' }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      ))}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('users:t.editEmail')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('users:t.deleteUser')}</ListItemText>
        </MenuItem>
      </Menu>

      {selectedUser && (
        <>
          <UserForm
            open={editOpen}
            onCancel={() => setEditOpen(false)}
            onSubmit={handleEditSubmit}
            defaultValues={selectedUser}
            buttonText={t('common:t.save')}
            loading={editUserLoading}
            cancelButton
          />
          <DeleteDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onConfirm={handleDeleteConfirm}
            loading={deleteUserLoading}
            ns="users"
            email={selectedUser.email}
          />
        </>
      )}
    </Grid>
  );
};

export default UserColumn;
